import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import CashierLogin from './CashierLogin';
import { toast } from "react-toastify";
import { getOrCreateDeviceId } from "../utils/device";
import {
  clearLockedSession,
  getLockedSession,
  migrateLegacyLockedSession,
} from "../utils/lockedSessions";
import {
  buildCloudLoginUrl,
  clearInvalidCloudSession,
} from "../utils/cloudBrowserSession";
import { isDefinitiveAuthenticationRejection } from "../utils/apiClient";
import { canUseCashSessionForRole } from "../utils/cashSessionRoles";

const normalizeRole = (value) => {
  const role = String(value || '').trim().toLowerCase();
  const map = {
    caisse: 'cashier',
    caissier: 'cashier',
    cashier: 'cashier',
    serveur: 'server',
    server: 'server',
    stockeur: 'stockeur',
    stock: 'stockeur',
    manager: 'manager',
    admin: 'admin',
    superadmin: 'superadmin',
    kitchen: 'kitchen',
    bar: 'bar',
  };
  return map[role] || role;
};

// /api/auth/me est volontairement une réponse d'identité minimale. Les
// métadonnées de portée ajoutées au login Cloud (notamment tenant_subdomain)
// doivent survivre à cette revalidation, sinon la garde PWA efface une session
// pourtant valide au prochain démarrage.
export const mergeRevalidatedUser = (storedUser, serverUser) => ({
  ...(storedUser && typeof storedUser === "object" ? storedUser : {}),
  ...(serverUser && typeof serverUser === "object" ? serverUser : {}),
});

export const canReturnToProtectedPath = ({
  requiredRole,
  userRole,
  currentPath,
}) => {
  const normalizedRole = normalizeRole(userRole);
  const normalizedRequiredRole = normalizeRole(requiredRole);
  const path = String(currentPath || "");

  return Boolean(
    (normalizedRequiredRole === "superadmin" &&
      normalizedRole === "superadmin" &&
      path.startsWith("/superadmin")) ||
      (normalizedRequiredRole === "admin" &&
        ["admin", "manager", "superadmin"].includes(normalizedRole) &&
        path.startsWith("/admin")) ||
      (normalizedRequiredRole === "stockeur" &&
        ["stockeur", "admin", "manager", "superadmin"].includes(normalizedRole) &&
        path.startsWith("/waise")) ||
      (normalizedRequiredRole === "cashier" &&
        ["cashier", "server"].includes(normalizedRole) &&
        path === "/caisse") ||
      (normalizedRequiredRole === "kitchen" &&
        ["kitchen", "manager", "admin"].includes(normalizedRole) &&
        path === "/cuisine") ||
      (normalizedRequiredRole === "bar" &&
        ["bar", "manager", "admin"].includes(normalizedRole) &&
        path === "/bar")
  );
};

export const shouldClearAuthenticationForToken = ({
  rejectedToken,
  storedToken,
  status,
}) =>
  Number(status) === 401 &&
  Boolean(String(rejectedToken || "")) &&
  String(rejectedToken || "") === String(storedToken || "");

export const isSameDeviceSessionConflict = ({
  activeDeviceId,
  currentDeviceId,
}) => {
  const active = String(activeDeviceId || "").trim();
  const current = String(currentDeviceId || "").trim();
  return Boolean(active && current && active === current);
};

const getServerParticipationValue = (pkg) => {
  if (!pkg || typeof pkg !== "object") return false;
  // Source explicite prioritaire : si le toggle a été défini, on le respecte strictement.
  if (typeof pkg.server_participation_enabled === "boolean") {
    return pkg.server_participation_enabled;
  }
  if (typeof pkg.serverParticipationEnabled === "boolean") {
    return pkg.serverParticipationEnabled;
  }
  if (typeof pkg.server_enabled === "boolean") {
    return pkg.server_enabled;
  }
  // Fallback compatible anciens formats : n'active QUE si le package expose clairement
  // un droit serveur, mais uniquement quand aucun toggle explicite n'est présent.
  if (pkg?.allowedRoles && typeof pkg.allowedRoles.server === "boolean") {
    return pkg.allowedRoles.server;
  }
  const roleLimitRaw = pkg?.roleLimits?.server;
  if (roleLimitRaw !== undefined && roleLimitRaw !== null) {
    const roleLimit = Number(roleLimitRaw);
    if (Number.isFinite(roleLimit)) {
      return roleLimit > 0;
    }
    const normalized = String(roleLimitRaw).trim().toLowerCase();
    if (normalized === "unlimited" || normalized === "illimite" || normalized === "illimité") {
      return true;
    }
  }
  return false;
};

const resolveServerParticipationFromPackages = (packages = [], activeId = "") => {
  if (!Array.isArray(packages) || packages.length === 0) return false;
  const activePackage =
    packages.find((pkg) => String(pkg?.id) === String(activeId || "")) ||
    packages[0] ||
    null;
  return getServerParticipationValue(activePackage);
};

const parsePackagesResponse = (payload) => {
  if (!payload) return { packages: [], activeId: "" };
  if (Array.isArray(payload)) {
    return { packages: payload, activeId: "" };
  }
  if (Array.isArray(payload?.packages)) {
    return { packages: payload.packages, activeId: String(payload?.activeId || "") };
  }
  return { packages: [], activeId: "" };
};

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [serverParticipationEnabled, setServerParticipationEnabled] = useState(false);
  const [serverParticipationChecked, setServerParticipationChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const authFailureCountRef = useRef(0);
  const packageAuthorityRetryTimerRef = useRef(null);
  const packageAuthorityRetryAttemptRef = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const storageUserKey = requiredRole === 'superadmin' ? 'moo_user_superadmin' : 'moo_user';
  const storageTokenKey = requiredRole === 'superadmin' ? 'moo_token_superadmin' : 'moo_token';

  const isCloudHost = () => {
    try {
      if (typeof window === "undefined") return false;
      const host = window.location?.hostname || "";
      if (!host) return false;
      if (host === "localhost" || host === "127.0.0.1" || host === "::1") return false;
      return host.includes("moosync");
    } catch (_e) {
      return false;
    }
  };

  const clearPackageAuthorityRetry = useCallback(() => {
    if (packageAuthorityRetryTimerRef.current !== null) {
      window.clearTimeout(packageAuthorityRetryTimerRef.current);
      packageAuthorityRetryTimerRef.current = null;
    }
    packageAuthorityRetryAttemptRef.current = 0;
  }, []);

  const schedulePackageAuthorityRetry = useCallback(() => {
    if (packageAuthorityRetryTimerRef.current !== null) return;
    const delay = Math.min(
      5000,
      750 * (2 ** packageAuthorityRetryAttemptRef.current),
    );
    packageAuthorityRetryAttemptRef.current += 1;
    packageAuthorityRetryTimerRef.current = window.setTimeout(() => {
      packageAuthorityRetryTimerRef.current = null;
      window.dispatchEvent(new Event("moo-auth-refresh"));
    }, delay);
  }, []);

  const resolveServerParticipationFromServer = useCallback(async () => {
    const response = await fetch(`/api/packages?_ts=${Date.now()}`, {
      cache: "no-store",
      headers: { "Cache-Control": "no-store" },
    });
    if (!response.ok || response.status === 304) {
      throw new Error(`package settings unavailable (${response.status})`);
    }
    const data = await response.json();
    const { packages, activeId } = parsePackagesResponse(data);
    const resolvedActiveId = activeId || String(packages[0]?.id || "");
    localStorage.setItem("moo_packages", JSON.stringify(packages));
    localStorage.setItem("moo_active_package", resolvedActiveId);
    clearPackageAuthorityRetry();
    return resolveServerParticipationFromPackages(packages, resolvedActiveId);
  }, [clearPackageAuthorityRetry]);

  const checkAuth = useCallback(async () => {
    let checkedUserRaw = null;
    let checkedToken = null;
    try {
      const savedUser = localStorage.getItem(storageUserKey);
      const savedToken = localStorage.getItem(storageTokenKey);
      checkedUserRaw = savedUser;
      checkedToken = savedToken;

      if (savedUser && savedToken) {
        const localUserData = JSON.parse(savedUser);
        let userData = localUserData;
        const host = String(window.location?.hostname || "");
        const isRemoteCloud =
          host &&
          !["localhost", "127.0.0.1", "0.0.0.0", "::1"].includes(host) &&
          !host.endsWith(".local") &&
          !host.endsWith(".lan");
        if (!isRemoteCloud || String(savedToken).startsWith("moo_")) {
          try {
            const meResponse = await fetch("/api/auth/me", {
              cache: "no-store",
              headers: {
                Authorization: `Bearer ${savedToken}`,
                "X-Moo-Device-Id": getOrCreateDeviceId(),
              },
            });
            if (isDefinitiveAuthenticationRejection(meResponse.status)) {
              const rejection = new Error("AUTH_SESSION_INVALID");
              rejection.definitiveAuthRejection = true;
              throw rejection;
            }
            if (meResponse.ok) {
              const mePayload = await meResponse.json();
              if (mePayload?.user?.id) {
                userData = mergeRevalidatedUser(localUserData, mePayload.user);
                localStorage.setItem(storageUserKey, JSON.stringify(userData));
              } else {
                console.warn(
                  "Réponse /api/auth/me incomplète; identité locale conservée",
                );
              }
            }
            // Sur 5xx, conserver temporairement l'identité déjà validée
            // localement. Les routes métier restent protégées côté serveur.
          } catch (authError) {
            if (authError?.definitiveAuthRejection) {
              throw authError;
            }
            // Une coupure, un timeout ou une réponse illisible ne prouve pas
            // que le jeton est expiré. Ne jamais déconnecter le caissier pour
            // une indisponibilité temporaire de l'API.
            console.warn(
              "Validation /api/auth/me momentanément indisponible; session locale conservée",
              authError,
            );
          }
        }

        const normalizedUserRole = normalizeRole(userData?.role);
        const normalizedRequiredRole = normalizeRole(requiredRole);
        const shouldCheckServerParticipation =
          normalizedUserRole === "server" && normalizedRequiredRole === "cashier";

        if (shouldCheckServerParticipation) {
          setServerParticipationEnabled(false);
          setServerParticipationChecked(false);
          try {
            const resolved = await resolveServerParticipationFromServer();
            setServerParticipationEnabled(Boolean(resolved));
            setServerParticipationChecked(true);
          } catch (_error) {
            schedulePackageAuthorityRetry();
          }
        } else {
          setServerParticipationEnabled(false);
          setServerParticipationChecked(true);
        }

        setUser(userData);
        setToken(savedToken);
      } else {
        setUser(null);
        setToken(null);
        setServerParticipationChecked(false);
      }
    } catch (error) {
      console.error('Erreur vérification auth:', error);
      const latestToken = localStorage.getItem(storageTokenKey);
      const latestUserRaw = localStorage.getItem(storageUserKey);
      const isDefinitiveRejection = Boolean(error?.definitiveAuthRejection);
      const tokenStillMatches =
        String(latestToken || "") === String(checkedToken || "");

      // Une ancienne validation peut finir après une nouvelle connexion. Elle
      // ne doit jamais supprimer le nouveau jeton qui vient d'être enregistré.
      if (isDefinitiveRejection && tokenStillMatches) {
        if (isCloudHost() && requiredRole !== "superadmin") {
          clearInvalidCloudSession(localStorage);
        } else {
          localStorage.removeItem(storageUserKey);
          localStorage.removeItem(storageTokenKey);
        }
        setUser(null);
        setToken(null);
      } else if (latestToken && latestUserRaw) {
        try {
          setUser(JSON.parse(latestUserRaw));
          setToken(latestToken);
        } catch (_parseError) {
          // Un stockage local illisible ne doit être effacé que s'il n'a pas
          // été remplacé entre-temps par une nouvelle connexion.
          if (tokenStillMatches && latestUserRaw === checkedUserRaw) {
            localStorage.removeItem(storageUserKey);
            localStorage.removeItem(storageTokenKey);
          }
          setUser(null);
          setToken(null);
        }
      } else {
        setUser(null);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  }, [
    requiredRole,
    resolveServerParticipationFromServer,
    schedulePackageAuthorityRetry,
    storageTokenKey,
    storageUserKey,
  ]);

  const resolveLatestServerParticipation = useCallback(async () => {
    setServerParticipationEnabled(false);
    setServerParticipationChecked(false);
    try {
      const resolved = await resolveServerParticipationFromServer();
      setServerParticipationEnabled(Boolean(resolved));
      setServerParticipationChecked(true);
      return Boolean(resolved);
    } catch (_error) {
      schedulePackageAuthorityRetry();
      return false;
    }
  }, [resolveServerParticipationFromServer, schedulePackageAuthorityRetry]);

  const closeActiveCashSessionOnLogout = useCallback(async () => {
    const userId = Number(user?.id);
    if (
      !Number.isFinite(userId) ||
      userId <= 0 ||
      !canUseCashSessionForRole(user?.role)
    ) return;
    try {
      const activeResponse = await fetch(`/api/cash-sessions/active`, {
        cache: "no-store",
      });
      if (!activeResponse.ok) return;
      const activeSession = await activeResponse.json();
      if (!activeSession?.id) return;

      let computedFinalAmount = Number(activeSession.initial_amount || 0);
      try {
        const summaryResponse = await fetch(
          `/api/cash-journal/summary/${activeSession.id}?cashierId=${userId}`,
          { cache: "no-store" },
        );
        if (summaryResponse.ok) {
          const summaryData = await summaryResponse.json();
          const theoretical =
            summaryData?.summary?.theoretical_balance ??
            (Number(summaryData?.summary?.total_revenue || 0) +
              Number(activeSession.initial_amount || 0));
          if (Number.isFinite(Number(theoretical))) {
            computedFinalAmount = Math.max(0, Math.round(Number(theoretical)));
          }
        }
      } catch (_e) {
        // fallback sur initial_amount
      }

      await fetch(`/api/cash-sessions/${activeSession.id}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          finalAmount: computedFinalAmount,
          notes: "Session fermée automatiquement lors de la déconnexion",
        }),
      });
    } catch (_e) {
      // non bloquant
    }
  }, [user?.id, user?.role]);

  // Une expiration/révocation du jeton n'est pas une déconnexion métier.
  // On oublie uniquement l'identité locale afin de demander un nouveau PIN,
  // sans fermer la session de caisse encore valide sur le serveur.
  const clearExpiredAuthentication = useCallback((rejectedToken) => {
    const latestToken = localStorage.getItem(storageTokenKey);
    if (
      !shouldClearAuthenticationForToken({
        rejectedToken,
        storedToken: latestToken,
        status: 401,
      })
    ) {
      const latestUserRaw = localStorage.getItem(storageUserKey);
      try {
        if (latestToken && latestUserRaw) {
          setUser(JSON.parse(latestUserRaw));
          setToken(latestToken);
        }
      } catch (_e) {
        // Le prochain checkAuth traitera un éventuel stockage illisible.
      }
      authFailureCountRef.current = 0;
      return false;
    }
    localStorage.removeItem(storageUserKey);
    localStorage.removeItem(storageTokenKey);
    setUser(null);
    setToken(null);
    authFailureCountRef.current = 0;
    return true;
  }, [storageTokenKey, storageUserKey]);

  // Une prise de contrôle distante doit fermer uniquement l'interface locale.
  // Elle ne doit pas appeler le logout métier, qui pourrait clôturer la session
  // de caisse encore utilisée par le nouvel appareil.
  const clearTakenOverLocalAuthentication = useCallback((rejectedToken) => {
    const latestToken = localStorage.getItem(storageTokenKey);
    if (
      rejectedToken &&
      latestToken &&
      String(rejectedToken) !== String(latestToken)
    ) {
      return false;
    }
    localStorage.removeItem(storageUserKey);
    localStorage.removeItem(storageTokenKey);
    setUser(null);
    setToken(null);
    authFailureCountRef.current = 0;
    return true;
  }, [storageTokenKey, storageUserKey]);

  const handleLogout = useCallback(
    async ({ silent = false } = {}) => {
      const existingToken = localStorage.getItem(storageTokenKey);
      try {
        await closeActiveCashSessionOnLogout();
        if (existingToken) {
          await fetch("/api/auth/logout", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${existingToken}`,
              "Content-Type": "application/json",
            },
          });
        }
      } catch (_e) {
        // non bloquant
      } finally {
        localStorage.removeItem(storageUserKey);
        localStorage.removeItem(storageTokenKey);
        setUser(null);
        setToken(null);
        if (!silent) {
          navigate("/", { replace: true });
        }
      }
    },
    [closeActiveCashSessionOnLogout, navigate, storageTokenKey, storageUserKey],
  );

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(
    () => () => {
      if (packageAuthorityRetryTimerRef.current !== null) {
        window.clearTimeout(packageAuthorityRetryTimerRef.current);
        packageAuthorityRetryTimerRef.current = null;
      }
    },
    [],
  );

  // Réagit aux changements d'auth entre onglets (storage event) + événements internes (même onglet).
  useEffect(() => {
    const handleStorage = (event) => {
      if (!event) return;
      if (
        event.key === storageUserKey ||
        event.key === storageTokenKey ||
        event.key === "moo_packages" ||
        event.key === "moo_active_package"
      ) {
        checkAuth();
      }
    };
    const handleAuthRefresh = () => checkAuth();
    window.addEventListener("storage", handleStorage);
    window.addEventListener("moo-auth-refresh", handleAuthRefresh);
    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("moo-auth-refresh", handleAuthRefresh);
    };
  }, [checkAuth, storageUserKey, storageTokenKey]);

  // Heartbeat: garantit 1 appareil / compte et détecte la prise de contrôle
  useEffect(() => {
    if (!user || !token) return undefined;
    // Une session utilisateur Cloud n'est pas une session d'appareil POS. Elle
    // est déjà validée par /api/auth/me et par le middleware de chaque route.
    // Ne jamais la soumettre au mécanisme "un appareil par compte" de la caisse.
    if (isCloudHost()) {
      const cloudToken = String(token || "");
      if (!cloudToken.startsWith("moo_") || cloudToken.startsWith("moo_cloud_user_")) {
        return undefined;
      }
    }

    const deviceId = getOrCreateDeviceId();
    let cancelled = false;

    const confirmAuthenticationRejected = async () => {
      try {
        const validationResponse = await fetch("/api/auth/me", {
          cache: "no-store",
          headers: {
            Authorization: `Bearer ${token}`,
            "X-Moo-Device-Id": deviceId,
          },
        });
        // /auth/me est le contrôle d'identité autoritaire. Une indisponibilité
        // serveur ou réseau ne doit jamais être interprétée comme une
        // déconnexion du caissier.
        return validationResponse.status === 401;
      } catch (_error) {
        return false;
      }
    };

    const ping = async () => {
      try {
        const res = await fetch("/api/auth/ping", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Moo-Device-Id": deviceId,
          },
          body: JSON.stringify({}),
        });

        if (cancelled) return;

        if (res.ok) {
          authFailureCountRef.current = 0;
          return;
        }

        if (res.status === 409) {
          const payload = await res.json().catch(() => null);
          const activeDeviceId = payload?.activeSession?.deviceId || null;

          // Cette protection vaut pour toutes les routes. Un heartbeat lancé
          // depuis /admin peut se terminer juste après la navigation vers
          // /caisse. Si le serveur désigne le même appareil, il ne s'agit pas
          // d'une prise de contrôle distante et il ne faut surtout pas appeler
          // handleLogout(), qui supprimerait le jeton fraîchement utilisé par
          // la nouvelle route.
          if (
            isSameDeviceSessionConflict({
              activeDeviceId,
              currentDeviceId: deviceId,
            })
          ) {
            const latestToken = localStorage.getItem(storageTokenKey);
            const latestUserRaw = localStorage.getItem(storageUserKey);
            if (latestToken && latestToken !== token) {
              try {
                if (latestUserRaw) {
                  setUser(JSON.parse(latestUserRaw));
                }
              } catch (_e) {
                // ignore
              }
              setToken(latestToken);
            }
            authFailureCountRef.current = 0;
            return;
          }

          // Caisse : on verrouille la session sur l'ancien appareil (sans déconnexion "hard")
          if (requiredRole === "cashier") {
            toast.error("Session verrouillée (compte ouvert ailleurs)");
            window.dispatchEvent(
              new CustomEvent("moo-session-taken-over", {
                detail: payload?.activeSession || null,
              }),
            );
            return;
          }

          toast.error("Compte ouvert sur un autre appareil");
          clearTakenOverLocalAuthentication(token);
          return;
        }

        if (res.status === 401) {
          const latestToken = localStorage.getItem(storageTokenKey);
          if (latestToken && latestToken !== token) {
            authFailureCountRef.current = 0;
            setToken(latestToken);
            return;
          }

          authFailureCountRef.current += 1;
          if (authFailureCountRef.current < 3) {
            return;
          }
          const authenticationRejected = await confirmAuthenticationRejected();
          if (!cancelled && authenticationRejected) {
            clearExpiredAuthentication(token);
          } else {
            // Le heartbeat peut être momentanément refusé pendant une reprise
            // réseau ou un renouvellement d'appareil. Tant que /auth/me ne
            // rejette pas le Bearer, conserver la connexion visible.
            authFailureCountRef.current = 0;
          }
          return;
        }

        if (res.status === 403) {
          // Un 403 prouve que l'utilisateur est authentifié mais n'a pas le
          // droit d'effectuer cette opération. Il ne doit jamais supprimer le
          // jeton ni renvoyer l'utilisateur au login.
          authFailureCountRef.current = 0;
          console.warn("Heartbeat refusé sans expiration de la connexion");
        }
      } catch (_e) {
        // Ne pas bloquer l'UI si la connexion a un souci temporaire
      }
    };

    // Ping initial + intervalle (POS LAN)
    ping();
    const interval = window.setInterval(ping, 8000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    clearExpiredAuthentication,
    clearTakenOverLocalAuthentication,
    handleLogout,
    requiredRole,
    storageTokenKey,
    storageUserKey,
    token,
    user,
  ]);

  const handleLogin = async (userData, serverToken) => {
    console.log('🔓 Connexion réussie pour:', userData);
    const normalizedUserRole = normalizeRole(userData?.role);
    const normalizedRequiredRole = normalizeRole(requiredRole);
    const shouldCheckServerParticipation =
      normalizedUserRole === "server" && normalizedRequiredRole === "cashier";
    if (shouldCheckServerParticipation) {
      await resolveLatestServerParticipation();
    } else {
      setServerParticipationChecked(true);
    }
    setUser(userData);
    setToken(serverToken || localStorage.getItem(storageTokenKey) || null);
    // Stocker dans l’espace dédié (admin vs superadmin)
    localStorage.setItem(storageUserKey, JSON.stringify(userData));
    if (serverToken) {
      localStorage.setItem(storageTokenKey, serverToken);
    }

    migrateLegacyLockedSession();
    const lockedSession = getLockedSession(userData?.id);
    const isUnlocking = lockedSession?.lockReason === "manual";
    if (lockedSession && !isUnlocking && userData?.id) {
      clearLockedSession(userData.id);
    }
    if (
      !isUnlocking &&
      String(userData?.role || "").toLowerCase() === "admin" &&
      userData?.id
    ) {
      sessionStorage.removeItem(`moo_admin_cash_drawer_shown_${userData.id}`);
    }
    
    // Redirection selon le rôle avec React Router (avec délai pour s'assurer de la mise à jour)
    setTimeout(() => {
      const redirectPaths = {
        admin: '/admin',
        manager: '/admin', // Les managers accèdent aussi à l'interface admin
        cashier: '/caisse',
        server: '/caisse', // Pour l'instant, les serveurs utilisent l'interface caisse
        kitchen: '/cuisine',
        bar: '/bar',
        stockeur: '/waise',
        superadmin: '/superadmin'
      };

      const currentPath = location?.pathname || "";
      const normalizedRole = normalizeRole(userData?.role);
      const defaultRedirectPath = redirectPaths[normalizedRole] || '/';
      const canReturnToCurrentPath = canReturnToProtectedPath({
        requiredRole,
        userRole: userData?.role,
        currentPath,
      });
      const redirectPath = canReturnToCurrentPath ? currentPath : defaultRedirectPath;
      console.log('📍 Redirection vers:', redirectPath);
      
      // Utiliser navigate au lieu de window.location.href
      navigate(redirectPath, { replace: true });
    }, 100);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // Si pas d'utilisateur connecté, afficher la page de login
  if (!user) {
    // Sur le cloud (tenant.*), l'auth admin/manager/stockeur se fait via /login (email/identifiant + mot de passe),
    // et non via le PIN local. On redirige donc vers /login pour éviter la confusion.
    if (
      isCloudHost() &&
      ["admin", "manager", "stockeur"].includes(normalizeRole(requiredRole))
    ) {
      return (
        <Navigate
          to={buildCloudLoginUrl({
            pathname: location.pathname,
            search: location.search,
          })}
          replace
        />
      );
    }
    const forceSuperadmin = requiredRole === 'superadmin';
    return (
      <CashierLogin
        onLogin={handleLogin}
        forceSuperadmin={forceSuperadmin}
        requiredRole={requiredRole}
      />
    );
  }

  // Vérifier les permissions de rôle si requis
  if (requiredRole) {
    const normalizedUserRole = normalizeRole(user.role);
    const normalizedRequiredRole = normalizeRole(requiredRole);

    // Superadmin: on force la navigation sur les routes dédiées /superadmin*.
    if (normalizedUserRole === "superadmin" && normalizedRequiredRole !== "superadmin") {
      return <Navigate to="/superadmin" replace />;
    }

    if (normalizedRequiredRole === 'superadmin' && normalizedUserRole !== 'superadmin') {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="bg-gray-800 rounded-xl p-8 text-center max-w-md">
            <div className="text-red-400 text-5xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-white mb-2">Accès réservé superadmin</h2>
            <p className="text-gray-400 mb-4">
              Vous devez être connecté en tant que superadmin pour accéder à cette section.
            </p>
            <button
              onClick={handleLogout}
              className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      );
    }
    const isPrivileged = ['admin', 'manager', 'superadmin'].includes(normalizedUserRole);
    const isCashierRoute = normalizedRequiredRole === 'cashier';
    const shouldResolveServerParticipation =
      isCashierRoute && normalizedUserRole === "server";
    if (shouldResolveServerParticipation && !serverParticipationChecked) {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-yellow-500/30 border-t-yellow-500 rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-400">Vérification des permissions serveur...</p>
          </div>
        </div>
      );
    }
    const isServerOnCaisse =
      isCashierRoute &&
      normalizedUserRole === 'server' &&
      Boolean(serverParticipationEnabled);
    const hasRequiredRole =
      normalizedUserRole === normalizedRequiredRole ||
      isPrivileged ||
      isServerOnCaisse;

    if (!hasRequiredRole) {
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="bg-gray-800 rounded-xl p-8 text-center max-w-md">
            <div className="text-red-400 text-5xl mb-4">🚫</div>
            <h2 className="text-2xl font-bold text-white mb-2">Accès refusé</h2>
            <p className="text-gray-400 mb-4">
              Vous n'avez pas les permissions pour accéder à cette section.
            </p>
            <button
              onClick={handleLogout}
              className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
            >
              Se déconnecter
            </button>
          </div>
        </div>
      );
    }
  }

  // Passer l'utilisateur et la fonction de déconnexion aux composants enfants
  if (!React.isValidElement(children)) {
    console.error('❌ Composant enfant invalide passé à ProtectedRoute:', children);
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="bg-gray-800 rounded-xl p-8 text-center max-w-md">
          <div className="text-red-400 text-5xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-white mb-2">Erreur composant</h2>
          <p className="text-gray-400">Le composant à afficher est invalide.</p>
        </div>
      </div>
    );
  }
  return React.cloneElement(children, { 
    currentUser: user, 
    onLogout: handleLogout 
  });
};

export default ProtectedRoute; 
